/**
 * IDE → web dashboard one-time ticket handoff (tkt_...).
 * Single path for index.html capture, AuthContext, ProtectedRoute, LoginPage, and Dashboard.
 */
import { apiFetch, API_JSON_ACCEPT, isApiMisrouteResponse } from './apiBase';
import { ssoWebClientAppId } from '../config/environments';
import {
  establishCookieSession,
  markCookieSession,
  setAuthSession,
  setAuthUserProfile,
  setCsrfToken,
  setIdeHandoffLegacySession,
} from './authTokenStore';

const PENDING_KEY = 'pending_auth_code';

/** @returns {string|null} */
export function readHandoffAuthCode() {
  try {
    const pending = localStorage.getItem(PENDING_KEY);
    if (pending && String(pending).startsWith('tkt_')) {
      return String(pending);
    }
  } catch (_) {
    /* ignore */
  }

  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const search = new URLSearchParams(window.location.search || '');
    let code = search.get('auth_code') || search.get('session_code');
    if (!code && window.location.hash && window.location.hash.indexOf('?') !== -1) {
      const hashQuery = window.location.hash.split('?')[1] || '';
      const hp = new URLSearchParams(hashQuery);
      code = hp.get('auth_code') || hp.get('session_code');
    }
    if (code && String(code).startsWith('tkt_')) {
      return String(code);
    }
  } catch (_) {
    /* ignore */
  }

  return null;
}

export function clearHandoffAuthCode() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function hasPendingIdeHandoff() {
  return Boolean(readHandoffAuthCode());
}

/**
 * Apply a successful /sessions/exchange JSON body to client session storage.
 * @returns {Promise<{ ok: boolean, mode?: string, user?: object|null, sessionToken?: string, reason?: string }>}
 */
export async function applyHandoffExchangePayload(data) {
  if (!data || !data.success) {
    return { ok: false, reason: 'invalid_payload' };
  }

  if (typeof data.csrf_token === 'string' && data.csrf_token) {
    setCsrfToken(data.csrf_token);
  }

  // ***** Code Start BH25082026_1810 - Require a real session (cookie or stored key)
  const wantsCookie =
    data.session_channel === 'cookie' || data.token_type === 'cookie';

  async function tryCookieSession() {
    try {
      const profile = await establishCookieSession();
      if (profile) {
        markCookieSession(true);
        setAuthUserProfile(profile);
        return { ok: true, mode: 'cookie', user: profile, cookieSession: true };
      }
    } catch (_) {
      /* cookie blocked or API unreachable — caller may fall back */
    }
    return null;
  }

  if (wantsCookie) {
    const cookieOk = await tryCookieSession();
    if (cookieOk) return cookieOk;
  }

  const sessionToken = data.session_token || data.api_key || null;
  const user = data.user || null;
  if (sessionToken) {
    const applied = setAuthSession(sessionToken, user);
    // Cookie-prefer SPA intentionally discards api_key — only count as success if stored
    if (applied.apiKey) {
      return {
        ok: true,
        mode: 'token',
        user: applied.user || user,
        sessionToken: applied.apiKey,
        cookieSession: false,
      };
    }
    // Exchange may still have set HttpOnly cookie even when JSON also had session_token
    if (!wantsCookie) {
      const cookieOk = await tryCookieSession();
      if (cookieOk) return cookieOk;
    }
    // ***** Code Start BH25082026_1812 - Cookie-prefer SPA: allow ticket key when cookie blocked
    const forced = setIdeHandoffLegacySession(sessionToken, user);
    if (forced.apiKey) {
      return {
        ok: true,
        mode: 'token_handoff',
        user: forced.user || user,
        sessionToken: forced.apiKey,
        cookieSession: false,
      };
    }
    // ***** Code End BH25082026_1812
    return { ok: false, reason: 'session_not_established' };
  }

  if (!wantsCookie) {
    const cookieOk = await tryCookieSession();
    if (cookieOk) return cookieOk;
  }

  if (user?.email) {
    setAuthUserProfile(user);
  }

  return { ok: false, reason: 'session_not_established' };
  // ***** Code End BH25082026_1810
}

/**
 * Exchange IDE handoff ticket with the API and establish a web session.
 * @param {string} [authCode] defaults to readHandoffAuthCode()
 * @param {{ source?: string }} [options]
 */
export async function exchangeIdeHandoffTicket(authCode, options = {}) {
  const code = (authCode || readHandoffAuthCode() || '').trim();
  if (!code.startsWith('tkt_')) {
    return { ok: false, reason: 'no_code' };
  }

  let response;
  try {
    // ***** Code Start BH28082026_1115 - Handoff exchange via apiFetch (SPA misroute recovery)
    response = await apiFetch('/api/v1/sessions/exchange', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: API_JSON_ACCEPT,
      },
      credentials: 'include',
      body: JSON.stringify({
        auth_code: code,
        context: {
          source: options.source || ssoWebClientAppId(),
          host: typeof window !== 'undefined' ? window.location.host : undefined,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        },
      }),
    });
    // ***** Code End BH28082026_1115
  } catch (err) {
    return {
      ok: false,
      reason: 'network_error',
      error: err instanceof Error ? err.message : String(err),
    };
  }

  let data = {};
  let bodyText = '';
  try {
    bodyText = await response.clone().text();
    data = bodyText ? JSON.parse(bodyText) : {};
  } catch (_) {
    data = {};
  }

  if (isApiMisrouteResponse(response, bodyText)) {
    clearHandoffAuthCode();
    return { ok: false, reason: 'api_unreachable', status: response.status };
  }

  if (!response.ok || !data.success) {
    const detail = data.detail || data.message;
    // ***** Code Start BH25082026_1715 - Stop retry storms on permanent / misrouted API failures
    // 400/401: ticket bad. 404/405/406/501/502/503: wrong host or API not proxied (e.g. IIS SPA).
    const permanent =
      response.status === 400 ||
      response.status === 401 ||
      response.status === 404 ||
      response.status === 405 ||
      response.status === 406 ||
      response.status === 501 ||
      response.status === 502 ||
      response.status === 503;
    if (permanent) {
      clearHandoffAuthCode();
    }
    // ***** Code End BH25082026_1715
    return {
      ok: false,
      reason: typeof detail === 'string' ? detail : `exchange_${response.status}`,
      status: response.status,
    };
  }

  const applied = await applyHandoffExchangePayload(data);
  if (applied.ok) {
    clearHandoffAuthCode();
    if (typeof window !== 'undefined') {
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (_) {
        /* ignore */
      }
    }
  }

  return { ...applied, status: response.status };
}
